import { useEffect, useMemo, useRef, useState } from 'react';
import { MoreVertical, Check, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';
import { cn } from '@shared/lib/cn';

export interface Column<T> {
  /** Stable key for the column. */
  key: string;
  header: React.ReactNode;
  /** Cell content for a row. */
  render: (row: T, index: number) => React.ReactNode;
  align?: 'left' | 'right' | 'center';
  /** Extra classes for the <td>. */
  className?: string;
  /** Extra classes for the <th>. */
  headerClassName?: string;
  /** Label shown in the column-visibility menu (falls back to a string header / key). */
  title?: string;
  /** If true, this column can't be hidden from the column menu. */
  alwaysVisible?: boolean;
  /**
   * Makes the header a sort toggle. Return what to order by — numbers sort
   * numerically, strings case-insensitively; null/undefined always sort last.
   */
  sortValue?: (row: T) => string | number | null | undefined;
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  isLoading?: boolean;
  isError?: boolean;
  loadingMessage?: string;
  errorMessage?: string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  /** Alternating row background. Default true. */
  zebra?: boolean;
  /** Extra classes per row (applied last, so it can override the zebra bg). */
  rowClassName?: (row: T, index: number) => string;
  /** Show the column-visibility (three-dots) control in the header. Default true. */
  columnToggle?: boolean;
  /** Enable a leading checkbox column for multi-row selection. */
  selectable?: boolean;
  /** Currently-selected row keys (controlled). */
  selectedKeys?: Set<string>;
  /** Called with the next selection whenever a checkbox toggles. */
  onSelectionChange?: (keys: Set<string>) => void;
  /** When selectable, restrict which rows get a checkbox (default: all). */
  isSelectable?: (row: T) => boolean;
  /** Tighter row/header padding for compact tables. Default false. */
  dense?: boolean;
  /** Override body/footer cell padding (e.g. 'px-4 py-1.5'). Wins over `dense`. */
  cellPadding?: string;
  /** Optional sticky footer cell per column (e.g. totals aligned under columns). */
  footer?: (column: Column<T>) => React.ReactNode;
}

const alignClass = (align?: Column<unknown>['align']) =>
  align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';

const colLabel = <T,>(c: Column<T>) =>
  c.title ?? (typeof c.header === 'string' && c.header ? c.header : c.key);

/**
 * Reusable table: sticky green header, zebra rows, sortable columns, a
 * column-visibility menu, and built-in loading / error / empty states. Fills its
 * parent's height and scrolls only the rows.
 *
 * Wrap in a flex column container (e.g. `flex flex-1 flex-col min-h-0`) so the
 * header stays fixed while the body scrolls.
 */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  isLoading,
  isError,
  loadingMessage = 'Loading…',
  errorMessage = 'Failed to load.',
  emptyMessage = 'No records found.',
  onRowClick,
  zebra = true,
  rowClassName,
  columnToggle = true,
  selectable = false,
  selectedKeys,
  onSelectionChange,
  isSelectable,
  dense = false,
  cellPadding,
  footer,
}: DataTableProps<T>) {
  const headPad = dense ? 'py-2' : 'py-2.5';
  const cellPad = dense ? 'px-4 py-2' : 'px-4 py-2.5';
  const cellHeadPad = dense ? 'px-4 py-2' : 'px-4 py-2.5';
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLTableCellElement>(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  const [sort, setSort] = useState<{ key: string; dir: 'asc' | 'desc' } | null>(null);

  /** Click cycles ascending → descending → back to the rows' own order. */
  const toggleSort = (key: string) =>
    setSort((prev) => {
      if (prev?.key !== key) return { key, dir: 'asc' };
      return prev.dir === 'asc' ? { key, dir: 'desc' } : null;
    });

  const sortedRows = useMemo(() => {
    const col = sort && columns.find((c) => c.key === sort.key);
    if (!sort || !col?.sortValue) return rows;
    const dir = sort.dir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = col.sortValue!(a);
      const bv = col.sortValue!(b);
      // Blanks sink to the bottom either way — an empty date is not "earliest".
      if (av == null || av === '') return bv == null || bv === '' ? 0 : 1;
      if (bv == null || bv === '') return -1;
      if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * dir;
      return (
        String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: 'base' }) * dir
      );
    });
  }, [rows, columns, sort]);

  const visibleColumns = columns.filter((c) => c.alwaysVisible || !hidden.has(c.key));
  const colSpan = visibleColumns.length + (columnToggle ? 1 : 0) + (selectable ? 1 : 0);

  const selected = selectedKeys ?? new Set<string>();
  const canSelect = isSelectable ?? (() => true);
  const selectableRows = selectable ? rows.filter(canSelect) : [];
  const allSelected =
    selectableRows.length > 0 && selectableRows.every((r) => selected.has(rowKey(r)));
  const toggleRow = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onSelectionChange?.(next);
  };
  const toggleAll = () => {
    onSelectionChange?.(allSelected ? new Set() : new Set(selectableRows.map(rowKey)));
  };

  const toggleColumn = (key: string) =>
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        // Keep at least one column visible.
        if (visibleColumns.length <= 1) return prev;
        next.add(key);
      }
      return next;
    });

  // The background sits on each <th> rather than the <tr>: a row background is
  // not painted behind sticky cells, so the body would show through on scroll.
  //
  // Never add `relative` to a header cell alongside this — tailwind-merge treats
  // the two as conflicting position utilities and the later one wins, silently
  // unsticking the entire header. `sticky` already establishes a containing
  // block for absolutely-positioned children such as the column menu.
  const headCell = 'sticky top-0 z-10 bg-periscope-600';

  return (
    <div className="min-h-0 flex-1 overflow-auto">
      <table className="w-full border-separate border-spacing-0">
        <thead>
          <tr>
            {selectable && (
              <th className={cn(headCell, 'w-8 pl-3 pr-0', headPad)}>
                <input
                  type="checkbox"
                  aria-label="Select all"
                  checked={allSelected}
                  onChange={toggleAll}
                  className="h-4 w-4 cursor-pointer rounded border-white/40 accent-white"
                />
              </th>
            )}
            {columnToggle && (
              <th ref={menuRef} className={cn(headCell, 'w-9 px-2', headPad)}>
                <button
                  type="button"
                  onClick={() => setMenuOpen((v) => !v)}
                  title="Show/hide columns"
                  className="flex h-6 w-6 items-center justify-center rounded-md text-white/80 transition-colors hover:bg-white/15 hover:text-white"
                >
                  <MoreVertical size={16} />
                </button>
                {menuOpen && (
                  <div className="absolute left-1 top-full z-30 mt-1 min-w-[12rem] rounded-lg border border-slate-200 bg-white p-1 text-left shadow-lg">
                    <p className="px-2 py-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
                      Columns
                    </p>
                    {columns.map((c) => {
                      const visible = c.alwaysVisible || !hidden.has(c.key);
                      return (
                        <button
                          key={c.key}
                          type="button"
                          disabled={c.alwaysVisible}
                          onClick={() => toggleColumn(c.key)}
                          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm font-normal text-slate-700 transition-colors hover:bg-slate-50 disabled:opacity-60"
                        >
                          <span
                            className={cn(
                              'flex h-4 w-4 items-center justify-center rounded border',
                              visible
                                ? 'border-periscope-700 bg-periscope-700 text-white'
                                : 'border-slate-300',
                            )}
                          >
                            {visible && <Check size={12} />}
                          </span>
                          <span className="truncate">{colLabel(c)}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </th>
            )}
            {visibleColumns.map((c) => {
              const sorted = sort?.key === c.key ? sort.dir : null;
              return (
                <th
                  key={c.key}
                  aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cn(
                    headCell,
                    'whitespace-nowrap text-[13px] font-semibold text-white',
                    cellHeadPad,
                    alignClass(c.align),
                    c.headerClassName,
                  )}
                >
                  {c.sortValue ? (
                    <button
                      type="button"
                      onClick={() => toggleSort(c.key)}
                      className={cn(
                        'group inline-flex items-center gap-1 rounded transition-colors hover:text-white/80',
                        c.align === 'right' && 'flex-row-reverse',
                      )}
                    >
                      {c.header}
                      {sorted === 'asc' ? (
                        <ChevronUp size={13} />
                      ) : sorted === 'desc' ? (
                        <ChevronDown size={13} />
                      ) : (
                        <ChevronsUpDown size={13} className="opacity-40 group-hover:opacity-70" />
                      )}
                    </button>
                  ) : (
                    c.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr>
              <td colSpan={colSpan} className="py-16 text-center text-slate-400">
                {loadingMessage}
              </td>
            </tr>
          ) : isError ? (
            <tr>
              <td colSpan={colSpan} className="py-16 text-center text-red-600">
                {errorMessage}
              </td>
            </tr>
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={colSpan} className="py-16 text-center text-slate-400">
                {emptyMessage}
              </td>
            </tr>
          ) : (
            sortedRows.map((row, i) => (
              <tr
                key={rowKey(row)}
                onClick={onRowClick ? () => onRowClick(row) : undefined}
                className={cn(
                  onRowClick && 'cursor-pointer',
                  // Zebra banding carries row separation, so there are no rules
                  // between rows to compete with it.
                  zebra && (i % 2 === 0 ? 'bg-white' : 'bg-slate-200/60'),
                  'transition-colors hover:bg-periscope-50',
                  rowClassName?.(row, i),
                )}
              >
                {selectable && (
                  <td className="w-8 pl-3 pr-0" onClick={(e) => e.stopPropagation()}>
                    {canSelect(row) && (
                      <input
                        type="checkbox"
                        aria-label="Select row"
                        checked={selected.has(rowKey(row))}
                        onChange={() => toggleRow(rowKey(row))}
                        className="h-4 w-4 cursor-pointer rounded border-slate-300 accent-periscope-700"
                      />
                    )}
                  </td>
                )}
                {columnToggle && <td className="w-9 px-2" />}
                {visibleColumns.map((c) => (
                  <td
                    key={c.key}
                    className={cn(
                      'text-[13px] text-slate-700',
                      cellPad,
                      alignClass(c.align),
                      c.className,
                      cellPadding,
                    )}
                  >
                    {c.render(row, i)}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
        {footer && !isLoading && !isError && (
          <tfoot>
            <tr>
              {selectable && (
                <td className="sticky bottom-0 border-t border-slate-200 bg-slate-50" />
              )}
              {columnToggle && (
                <td className="sticky bottom-0 border-t border-slate-200 bg-slate-50" />
              )}
              {visibleColumns.map((c) => (
                <td
                  key={c.key}
                  className={cn(
                    'sticky bottom-0 border-t border-slate-200 bg-slate-50 font-semibold text-slate-900',
                    cellPad,
                    alignClass(c.align),
                    c.className,
                    cellPadding,
                  )}
                >
                  {footer(c)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}
